<template>
  <div class="!pt-0">
    <div class="flex flex-col space-y-2.5 max-h-screen pt-16 pb-2.5">
      <!-- Main Content Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto mt-2.5 relative">
        <!-- Header with Status and Actions -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-blue-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
                </div>
              </div>

              <div class="flex-1">
                <div class="flex items-center space-x-3">
                  <h1 class="text-2xl font-bold text-gray-900">
                    {{ $t('HeadContract.IndirectProgressClaimsShow.title') }}
                  </h1>

                  <template
                    v-if="claim"
                  >
                    <span class="text-gray-400">•</span>

                    <h2 class="text-lg font-semibold text-gray-900">
                      {{ $t('HeadContract.IndirectProgressClaimsShow.claimNumber', { claimNumber: claim.claim_number }) }}
                    </h2>

                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getStatusBadgeClass(claim.status)"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full mr-1.5"
                        :class="getStatusBadgeDotClass(claim.status)"
                      />
                      {{ $t(`Models.Claim.statusTitles.${enumKeyFromValue(ClaimStatus, claim.status)}`) }}
                    </span>
                  </template>
                </div>

                <div class="flex items-center space-x-3">
                  <p
                    v-if="claim"
                    class="text-sm text-gray-600 mt-1"
                  >
                    {{ new Date(claim.start).toDateString() }} - {{ new Date(claim.end).toDateString() }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center space-x-3">
              <!-- Certify Button for Draft Status -->
              <button
                v-if="claim?.status === ClaimStatus.draft && trades.length > 0"
                type="button"
                class="btn btn--primary-blue flex items-center space-x-2"
                @click="certifyClaim()"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>

                <span>{{ $t('HeadContract.IndirectProgressClaimsShow.button.certify') }}</span>
              </button>

              <button
                v-if="!userStore.isClientManager() && claim && claim.status !== ClaimStatus.certified && projectsStore.project?.unarchived"
                type="button"
                class="btn btn--secondary-blue flex items-center space-x-2"
                @click="requestApproval(claim.id)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
</svg>

                <span>{{ $t('HeadContract.IndirectProgressClaimsShow.button.requestApproval') }}</span>
              </button>

              <!-- Action Buttons for Draft Status -->
              <div
                v-if="claim?.status === ClaimStatus.draft"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-alert flex items-center space-x-2"
                  @click.prevent="deleteClaim"
                >
                  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>

                  <span>{{ $t('HeadContract.IndirectProgressClaimsShow.button.delete') }}</span>
                </button>
              </div>

              <!-- Action Buttons for Certified Status -->
              <div
                v-else-if="claim?.status === ClaimStatus.certified"
                class="flex items-center space-x-2"
              >
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  :class="{ 'opacity-50': claimExportAction.is(States.LOADING) }"
                  :disabled="claimExportAction.is(States.LOADING)"
                  @click.prevent="exportClaim"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                  <span>{{ $t('HeadContract.IndirectProgressClaimsShow.button.export') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <ClaimTable
          v-if="trades.length > 0"
          :project-id="projectId"
          :claim-id="claimId"
        />

        <div
          v-else
          class="flex flex-col items-center space-y-4 p-6"
        >
          <p class="font-semibold">
            {{ $t('HeadContract.IndirectProgressClaimsShow.noDevelopmentCostsFound') }}
          </p>

          <RouterLink
            :to="{ name: 'IndirectBillOfQuantities' }"
            class="btn btn--secondary-blue"
          >
            {{ $t('HeadContract.IndirectProgressClaimsShow.button.addDevelopmentCosts') }}
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ClaimTable from '@/components/HeadContract/IndirectProgressClaimsShow/ClaimTable.vue';
import { promptCertificateLayout } from '@/composables/useCertificateLayoutSwal';
import { useFormData } from '@/composables/useFormData';
import { useProjectTypeRedirect } from '@/composables/useProjectTypeRedirect';
import { useRequestClaimApproval } from '@/composables/useRequestClaimApproval';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { enumKeyFromValue, timeZone } from '@/helpers';
import { ClaimStatus } from '@/models/Claim';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import Swal from 'sweetalert2';
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  claimId: string;
}>();

const claimsStore = useClaimsStore();
const router = useRouter();
const tradesStore = useTradesStore();
const projectsStore = useProjectsStore();
const userStore = useUserStore();
const { requestApproval } = useRequestClaimApproval();
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);

const trades = computed(() => {
  return tradesStore.models.where('boq_type', 'indirect').where('project_id', parseInt(props.projectId)).get();
});

fetchTradesAction.request(props.projectId, { boq_type: 'indirect' }).catch((error) => {
  console.log(error);
});

watch(() => props.projectId, (newValue, oldValue) => {
  if(newValue !== oldValue) {
    router.push({ name: 'IndirectProgressClaimsIndex' });
  }
});

watch(() => props.claimId, () => {
  claimsStore.fetchClaim(props.claimId);
}, { immediate: true });

const claim = computed(() => {
  // TODO: 10/11/23 constrain variation orders to only those with cost for the current claim
  if(props.claimId) {
    const claimQuery = claimsStore.models
      .with('trades', (query) => {
        query.with('trade_items')
          .where('boq_type', 'direct');
      })
      .with('trade_item_costs')
      .where(
        'project_id',
        parseInt(props.projectId),
      );

    return claimQuery.find(parseInt(props.claimId));
  }

  return undefined;
});

const certifyClaimAction = useStoreApiAction(claimsStore.certifyLite);

const certifyClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Are you sure?',
    text: 'Are you sure you want to certify this claim? You will not be able to adjust any amounts after certifying.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      certifyClaimAction.request(props.claimId, { submitted_at_time_zone: timeZone }).then(() => {
        Swal.fire({
          title: 'Claim Certified',
          icon: 'success',
        });
      }).catch((error) => {
        if(error.status === 422) {
          Swal.fire({
            title: 'Failed to certify claim',
            text: error.data.message,
            icon: 'error',
          });
        } else {
          Swal.fire({
            title: 'Failed to certify claim',
            icon: 'error',
          });
        }
      });
      // claimsStore.sendClaim(claim.value.id, { status: result.value });
    }
  });
};

const updateForm = useFormData({
  retention: 0,
});

watch(claim, (newValue) => {
  if(newValue) {
    updateForm.setData(newValue);
  }
});

const deleteClaim = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Claim?',
    text: 'Are you sure you want to delete this claim? This cannot be undone.',
    showCancelButton: true,
    showConfirmButton: true,
  }).then(() => {
    claimsStore.deleteClaim(claim.value.id).then(() => {
      router.push({ name: 'IndirectProgressClaimsIndex' });

      Swal.fire({
        icon: 'success',
        title: 'Claim deleted.',
      });
    });
  });
};

const claimExportAction = useStoreApiAction(claimsStore.export);

const exportClaim = async () => {
  const compact = await promptCertificateLayout();

  if(compact === undefined) {
    return;
  }

  claimExportAction.request(claim.value.id, { compact }).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Claim Exported.',
      text: 'Claim is being exported to PDF and will be sent to your email when it is ready.',
    });
  }).catch((error) => {
    if(error.status === 429) {
      Swal.fire({
        icon: 'error',
        title: 'PDF is currently being exported',
        text: error.data.message,
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Failed to Export.',
      });
    }
  });
};

// Status badge helper functions
const getStatusBadgeClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-100 text-green-800';

    default:
      return 'bg-red-100 text-red-800';
  }
};

const getStatusBadgeDotClass = (status: ObjectValues<typeof ClaimStatus>) => {
  switch (status) {
    case ClaimStatus.certified:
      return 'bg-green-400';

    default:
      return 'bg-red-400';
  }
};

useProjectTypeRedirect();
</script>

<style scoped></style>
